import {css, useTheme} from '@mui/material';
import {ArrayUtils, DlMuiDynamicIcon, GetStylesFunction, isNullsy} from '@wallaby-ui/core';
import {isEqual} from 'lodash';
import React from 'react';

import {Formatters} from './LightFormatter';

export type SelectedFilter = {
  name: string | string[];
  label: string;
  value: string | null | undefined;
  defaultValue?: any | any[];
  /** Id of the filter-panel tab the filter lives in (PDM-2750) - drives the per-tab badge counts. */
  tab?: string;
};
type Props = {
  selectedFilters: SelectedFilter[];
  tableFilters: Record<string, any>;
  changeTableFilters: (newTableFilters: Record<string, any>) => void;
  formatters: Formatters;
};
/** A filter shows as a chip once any of its parts holds a value. */
export function isSelectedFilterVisible(v: SelectedFilter, tableFilters: Record<string, any>): boolean {
  return ArrayUtils.asArray(v.name).some((w) => {
    const value = tableFilters[w];
    // NOTE: uncomment if you need to hide `[]`, however BE mostly doesn't support it anyways
    // if (Array.isArray(value) && value.length === 0 && Array.isArray(v.defaultValue) && v.defaultValue.length === 0) return false;
    return !isNullsy(value);
  });
}
export type SelectedFiltersCount = {
  /** Number of chips the pane renders (the "Active filters" badge). */
  total: number;
  /** Chips per filter-panel tab, keyed by SelectedFilter.tab (filters without a tab are only in `total`). */
  byTab: Record<string, number>;
};
/** Counts the chips the pane would render, in total and per tab, from the same inputs the pane gets. */
export function countSelectedFilters(selectedFilters: SelectedFilter[], tableFilters: Record<string, any>): SelectedFiltersCount {
  const byTab: Record<string, number> = {};
  let total = 0;
  selectedFilters.forEach((v) => {
    if (!isSelectedFilterVisible(v, tableFilters)) return;
    total += 1;
    if (v.tab) byTab[v.tab] = (byTab[v.tab] ?? 0) + 1;
  });
  return {total, byTab};
}
export const LightSelectedFiltersPane: React.FC<Props> = (props) => {
  const {selectedFilters, tableFilters, changeTableFilters, formatters} = props;
  const {t} = formatters;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const isVisible = (v: SelectedFilter) => isSelectedFilterVisible(v, tableFilters);
  const isClearable = (v: SelectedFilter) => {
    if (Array.isArray(v.name)) {
      return v.name.some((w, i) => isNullsy(v.defaultValue?.[i]) || !isEqual(tableFilters[w], v.defaultValue[i]));
    }
    return isNullsy(v.defaultValue) || !isEqual(tableFilters[v.name as string], v.defaultValue);
  };
  return (
    <div css={muiStyles.chipWrapper}>
      {selectedFilters.filter(isVisible).map((v) => (
        <div
          key={Array.isArray(v.name) ? v.name.join('-') : v.name}
          css={muiStyles.chip}
          style={{
            // The chip look must be identical in HAIDI and MERIE (PDW-155): the fallbacks are the
            // HAIDI values of gray-100/gray-500 — MERIE resolves those theme vars to darker colors
            // (#DDE1E6/#697077), so referencing them directly cannot give a uniform look. A theme
            // can still override via the dedicated --dlSelectedFilterChip* variables.
            background: 'var(--dlSelectedFilterChipBackground, #F4F6FA)',
            color: 'var(--dlSelectedFilterChipColor, #9BA8BD)',
            paddingRight: isClearable(v) ? undefined : 10,
          }}
        >
          <span css={muiStyles.chipText}>
            {v.label}: {v.value}
          </span>
          {isClearable(v) && (
            <DlMuiDynamicIcon
              iconName='haidiCloseIcon2'
              title={t('filter.remove')}
              useFixedViewBox={12}
              onClick={() => {
                const defaultEntries = ArrayUtils.zip(
                  Array.isArray(v.name) ? v.name : [v.name],
                  Array.isArray(v.name) ? v.defaultValue : [v.defaultValue] // NOTE: defaultValue can be a number[]
                );
                const defaultFilters = Object.fromEntries(defaultEntries);
                changeTableFilters({
                  ...tableFilters,
                  ...defaultFilters,
                });
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              noButtonStyles
              inputCss={muiStyles.chipIconButton}
              svgIconInputCss={muiStyles.chipIcon}
            />
          )}
        </div>
      ))}
    </div>
  );
};
const getStyles: GetStylesFunction = (options) => {
  return {
    chipWrapper: css`
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 8px;
      flex-wrap: wrap;
    `,
    chip: css`
      max-width: 100%;
      min-height: 32px;
      border-radius: 8px;
      padding: 10px 10px 10px;
      display: inline-flex;
      justify-content: flex-start;
      align-items: center;
    `,
    chipText: css`
      border-radius: 4px;
      margin: 0;
      font-family: SourceSansPro-Regular;
      font-size: 14px;
      line-height: 1.1;
      text-wrap: wrap;
      word-break: break-word;
      text-align: left;
    `,
    chipIconButton: css`
      padding: 0 10px 0 8px;
      /* follow the chip text color (set inline on the chip div) */
      color: inherit;
    `,
    chipIcon: css`
      width: 12px;
      height: 12px;
    `,
  };
};
